import React, { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { Offer } from 'common/contracts';
import { EFeatureFlag, EPopupSubType, OfferType } from 'constants/enums';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { useOfferTable } from '../../hooks/useOfferTable';
import { OfferPageLayout } from '../../components/OfferPageLayout';

import { usePopupsColumns } from './hooks/usePopupsColumns';

const PopupsTable = (): React.ReactElement => {
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });

  const {
    getOffers,
    pricingsResult,
    isPricingsLoading,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    externalIdFilter,
    setExternalIdFilter,
    nameFilter,
    setNameFilter,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    processOffers
  } = useOfferTable({
    offerType: OfferType.POPUP,
    offerLabel: 'Popup',
    needsPricings: true
  });

  const { columns } = usePopupsColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions,
    isPricingsLoading,
    pricingsResult
  });

  const hasFeatureFlagPostPurchase =
    featureFlags?.[EFeatureFlag.DASHBOARD_POST_PURCHASE];

  const offers: Offer[] = useMemo((): Offer[] => {
    if (getOffers.isLoading || !getOffers.data) {
      return [];
    }

    if (getOffers.data.offers) {
      return getOffers.data.offers
        .map((offer: Offer) => ({
          _id: offer.publisherOfferId,
          ...offer
        }))
        .filter((offer: Offer) =>
          hasFeatureFlagPostPurchase || offer.subType !== EPopupSubType.POST_PURCHASE
        );
    }

    if (getOffers.data.result) {
      return getOffers.data.result
        .filter((offer: Offer) => offer.type === 'SpecialOffer')
        .map((offer: Offer) => ({
          _id: offer.publisherOfferId,
          ...offer
        }));
    }

    return [];
  }, [getOffers.isLoading, getOffers.data, hasFeatureFlagPostPurchase]);

  const processed = useMemo(
    () => processOffers(offers),
    [offers, processOffers]
  );

  return (
    <OfferPageLayout
      title="Popups"
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      offerLabel="Popups"
      showFilters={offers.length > 0}
      externalIdFilter={externalIdFilter}
      onExternalIdFilterChange={setExternalIdFilter}
      nameFilter={nameFilter}
      onNameFilterChange={setNameFilter}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle="Add your first Popup"
      emptyStateDescription="Rewards that appear as popups when players open the webstore, encouraging them to return regularly"
      createButtonPrefix="Popup"
      bodyDataTestId="dashPopupsList"
      offerType={OfferType.POPUP}
      offerDesignType={OfferDesignTypes.PopUp}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle="Delete Popup"
      deleteModalDescription="Are you sure you want to delete this popup?"
    />
  );
};

export default PopupsTable;
